import React, { useEffect, useState } from "react";
import PriceModeCard from "@components/molecules/PriceModeCard";
import { useTheme } from "styled-components/native";
import { Spacer } from "@components/atoms";
import {
  Keyboard,
  KeyboardAvoidingView,
  Platform,
  ScrollView,
} from "react-native";
import { useUpdateUser } from "@apis/user";
import { useUserInfo } from "@redux/slices/userInfoSlice";
import { PriceModes } from "@components/molecules/PriceModeCard/types";
import Loader from "@components/atoms/Loader";
import { useTranslation } from "react-i18next";
import { SpaceType } from "@components/atoms/Spacer/types";
import styled from "styled-components/native";
import { useSelector } from "react-redux";
import LayoutContainer from "@components/layout/LayoutContainer";
import { useUserAnalytics } from "../../hooks/useAnalytics";
import { PriceModeEventType } from "@utils/analytics.web";
import { showToast, ToastTypes } from "@components/molecules/VDBToast/util";

export type ChangePriceModeScreenProps = {
  removeExtraSpacing?: boolean;
  closeModal: () => void;
  setPriceModeUpdate?: any;
  setToastMessage?: React.Dispatch<React.SetStateAction<string | null>>;
};

const ChangePriceModeScreen = ({
  removeExtraSpacing,
  closeModal,
  setPriceModeUpdate,
  setToastMessage,
}: ChangePriceModeScreenProps) => {
  const theme = useTheme();
  const userInfo = useUserInfo();
  const updateUser = useUpdateUser(userInfo.getUserId());
  const [checkedPriceMode, setCheckedPriceMode] = useState<number>(
    PriceModes.WHITE
  );
  const [showMode, setShowMode] = useState(false);
  const [orangeModeMarkup, setOrangeModeMarkup] = useState(false);
  const { t } = useTranslation();
  const installationIdentifier = useSelector(
    (state: any) => state.appState?.installationIdentifier
  );
  const { trackWebEvent } = useUserAnalytics();

  const ParentContainer = theme.isLargeScreen
    ? React.Fragment
    : LayoutContainer;

  useEffect(() => {
    setCheckedPriceMode(userInfo.user?.price_mode);
    setShowMode(
      !userInfo.forceGreenPriceProfile() &&
        userInfo.priceProfileVisibility() &&
        !userInfo.isPriceModeLock()
    );
    setOrangeModeMarkup(
      !userInfo.forceGreenPriceProfile() &&
        !userInfo.priceProfileVisibility() &&
        userInfo.userCanSetMarkupInOrangeMode()
    );
  }, [userInfo.user.price_mode, theme.isMobile]);

  const priceModeCardCount =
    (showMode ? 1 : 0) +
    (showMode && !userInfo?.removeGreenModeForCompany() ? 1 : 0) +
    (showMode || orangeModeMarkup ? 1 : 0);

  const updatePriceMode = (price_mode: PriceModes) => {
    Keyboard.dismiss();
    if (checkedPriceMode !== price_mode) {
      trackWebEvent(PriceModeEventType.CHANGE_PRICE_MODE, {
        previous_price_mode: price_mode,
      });

      updateUser.mutate(
        {
          user: {
            price_mode: price_mode,
            installation_identifier: installationIdentifier,
          },
        },
        {
          onSuccess() {
            theme.isLargeScreen &&
              price_mode !== PriceModes.ORANGE &&
              closeModal();
            setCheckedPriceMode(price_mode);
            theme.isLargeScreen
              ? setPriceModeUpdate(true)
              : showToast(
                  t("priceMode.priceMode.toastMessageSuccess.title"),
                  t("priceMode.priceMode.toastMessageSuccessDescription.title"),
                  ToastTypes.SUCCESS,
                  true,
                  true
                );
          },
        }
      );
    }
  };

  return (
    <ParentContainer>
      <KeyboardAvoidingView
        style={{
          flex: 1,
        }}
        {...(priceModeCardCount > 1 && {
          behavior: Platform.OS === "ios" ? "position" : "position",
          keyboardVerticalOffset:
            Platform.OS === "android" && SpaceType.Size_80,
        })}
      >
        <ScrollView
          style={{
            height: "100%",
            backgroundColor: theme[theme.isLargeScreen ? "core-01" : "core-02"],
            paddingLeft: SpaceType.ExtraSmall,
            paddingRight: SpaceType.ExtraSmall,
          }}
          keyboardShouldPersistTaps="handled"
        >
          <Container removeExtraSpacing={removeExtraSpacing}>
            {!removeExtraSpacing && (
              <>
                <Spacer size={SpaceType.Double} />
              </>
            )}
            {showMode && (
              <PriceModeCard
                title={t("priceMode.whiteMode.title")}
                description={t("priceMode.whiteMode.description")}
                checked={checkedPriceMode === PriceModes.WHITE}
                onPress={() => {
                  updatePriceMode(PriceModes.WHITE);
                }}
                labelColor={
                  checkedPriceMode === PriceModes.WHITE
                    ? theme["core-05"]
                    : theme["core-06"]
                }
              />
            )}
            {showMode && !userInfo?.removeGreenModeForCompany() && (
              <PriceModeCard
                title={t("priceMode.greenMode.title")}
                description={t("priceMode.greenMode.description")}
                checked={checkedPriceMode === PriceModes.GREEN}
                onPress={() => {
                  updatePriceMode(PriceModes.GREEN);
                }}
                labelColor={theme["success-01"]}
              />
            )}
            {(showMode || orangeModeMarkup) && (
              <PriceModeCard
                title={t("priceMode.orangeMode.title")}
                description={t("priceMode.orangeMode.description")}
                checked={checkedPriceMode === PriceModes.ORANGE}
                onPress={() => {
                  updatePriceMode(PriceModes.ORANGE);
                }}
                labelColor={theme["warn-01"]}
                isOrange={true}
                closeModal={() => {
                  closeModal();
                }}
                setToastMessage={
                  theme.isLargeScreen ? setToastMessage : undefined
                }
              />
            )}
          </Container>

          {!removeExtraSpacing && (
            <>
              <Spacer size={SpaceType.Size_80} />
            </>
          )}
        </ScrollView>
      </KeyboardAvoidingView>
      {updateUser.isLoading && (
        <Loader backgroundColor={theme["core-02"]} opacity={0.8} />
      )}
    </ParentContainer>
  );
};

const Container = styled.View<{ removeExtraSpacing?: boolean }>`
  padding-horizontal: ${({ theme, removeExtraSpacing }) =>
    removeExtraSpacing ? 0 : theme.isMobile ? 16 : 40}px;
  padding-top: 4px;
  padding-bottom: 4px;
  gap: 16px;
`;

export default ChangePriceModeScreen;
